
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  getMainWindowPath,
  isMainWindow,
  isPopupWindow,
  isTauri,
  saveMainWindowPath,
  takeUpdateRestartPath,
} from '@/lib/utils/tauri';
import { createLogger } from '@/lib/logger';

const logger = createLogger('hooks/usePageRestoration');

/**
 * Hook to save the current route to Tauri storage (for main window only)
 * This enables restoring the last visited page when the app is reopened
 */
export function useRouteTracking() {
  const location = useLocation();
  const lastSavedPath = useRef<string | null>(null);

  useEffect(() => {
    // Only run in Tauri and not in popup windows
    if (!isTauri() || isPopupWindow()) {
      return;
    }

    const trackRoute = async () => {
      // Check if this is the main window
      const isMain = await isMainWindow();
      if (!isMain) return;

      const currentPath = location.pathname + location.search + location.hash;

      // Don't save if it's the same as the last saved path
      if (currentPath === lastSavedPath.current) return;

      // Save the current path
      await saveMainWindowPath(currentPath);
      lastSavedPath.current = currentPath;
    };

    trackRoute();
  }, [location.pathname, location.search, location.hash]);
}

/**
 * Hook to restore the last visited page on app startup (for main window only)
 * Returns true if a restore navigation was initiated
 */
export function usePageRestore(): boolean {
  const navigate = useNavigate();
  const location = useLocation();
  const hasRestored = useRef(false);
  const didNavigate = useRef(false);

  useEffect(() => {
    // Only run once, only in Tauri, and only if not already restored
    if (hasRestored.current || !isTauri() || isPopupWindow()) {
      return;
    }

    const restore = async () => {
      // Check if this is the main window
      const isMain = await isMainWindow();
      if (!isMain) return;

      hasRestored.current = true;

      try {
        // Consume this even when an explicit startup route wins, so an update
        // return path can never leak into a later, unrelated launch.
        const updateRestartPath = await takeUpdateRestartPath();

        // Only restore if we're on the root path (initial app load).
        // This prevents overriding deep links or normal navigation.
        if (location.pathname !== '/' && location.pathname !== '') {
          return;
        }

        const isSafeUpdateRestartPath =
          updateRestartPath?.startsWith('/') &&
          !updateRestartPath.startsWith('//') &&
          !updateRestartPath.includes('\\');
        const savedPath = isSafeUpdateRestartPath
          ? updateRestartPath
          : await getMainWindowPath();

        if (savedPath && savedPath !== '/' && savedPath !== '') {
          // Normal last-page restoration remains limited to org-scoped paths.
          // The one-time update path is allowed to restore global app routes.
          const segments = savedPath.split(/[?#]/)[0].split('/').filter(Boolean);
          if (!isSafeUpdateRestartPath && segments.length < 2) {
            logger.info(`Skipping restore of non-org path: ${savedPath}`);
            return;
          }

          logger.info(
            `${isSafeUpdateRestartPath ? 'Restoring page after update' : 'Restoring last page'}: ${savedPath}`
          );
          didNavigate.current = true;
          navigate(savedPath, { replace: true });
        }
      } catch (error) {
        logger.error('Failed to restore page:', error);
      }
    };

    // Small delay to ensure the app is fully loaded
    const timeout = setTimeout(restore, 100);
    return () => clearTimeout(timeout);
  }, [navigate, location.pathname]);

  return didNavigate.current;
}
